import { cloudProviders, Region } from 'portal-shared-lib';
import { Event, FilespaceStorage } from 'fs-api-client';
import { createDefaultApi, createNoSecurityApi } from './createApi';
import { authenticationService } from '../services/authentication';
import { Nullable } from './types';
import * as Amplitude from '@amplitude/analytics-browser';

export function getRegionName(provider: FilespaceStorage.ProviderEnum, region: string): string {
    const cloudProvider = cloudProviders[provider];
    if (typeof cloudProvider === 'undefined') {
        return region;
    }

    if (!cloudProviders[provider].regionsList.length) {
        return region;
    }
    const regions = cloudProvider.regionsList;

    let regionFound = regions.find((reg: Region) => reg.region === region);
    if (typeof regionFound === 'undefined') {
        return '';
    }
    return regionFound.name;
}

export function formatDataSize(bytes: number = 0, fractionDigits: number = 2, stripTrailingZeroes: boolean = false) {
    if (bytes === 0) {
        return '0 Bytes';
    }

    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
        k = 1024,
        i = Math.floor(Math.log(bytes) / Math.log(k));

    let size = (bytes / Math.pow(k, i)).toFixed(fractionDigits);
    if (stripTrailingZeroes) {
        size = parseFloat(size).toString();
    }

    return size + ' ' + units[i];
}

export function formatEpochTimeInMiliSecondsToReadable(epochTime: number) {
    return new Date(epochTime).toLocaleString();
}

export function formatEpochTimeInSecondsToReadable(epochTime: number) {
    return new Date(epochTime * 1000).toLocaleString();
}

function getDateFormatWithoutDay(milliseconds: number): string;
function getDateFormatWithoutDay(date: Date): string;
function getDateFormatWithoutDay(param: Date | number): string {
    let date = param instanceof Date ?
        param :
        new Date(param);

    return date.toDateString().split(' ').slice(1).join(' ');
}
export { getDateFormatWithoutDay };

export function bindObjectMethods(target: any, prototype: any): void {
    const ignoreList = [ 'constructor', 'componentDidMount', 'render' ];
    const methodNames = Object.getOwnPropertyNames(prototype).filter((key) => {
        return prototype[key] &&
            typeof prototype[key] === 'function' &&
            !ignoreList.includes(key);
    });
    for (const methodName of methodNames) {
        target[methodName] = prototype[methodName].bind(target);
        target[methodName].originalName = methodName;
    }
}

export function beautifyMessageIfError(message: any): string {
    if (typeof message === 'string') {
        let messageJson = null;
        try {
            messageJson = JSON.parse(message);
        } catch (e) {}
        if (messageJson) { message = messageJson; }
    }

    if (typeof message !== 'string') {
        if (message.stack) { message.duplicateStackTrace = message.stack; }
        message = JSON.parse(JSON.stringify(message));

        const response = message.response;
        if (response) {
            delete response.headers;
            const request = response.request;
            if (request && request.uri && request.uri.href) {
                message.href = request.uri.href;
            }

            if (
                response.body &&
                typeof response.body === 'string'
            ) {
                if (
                    response.body.indexOf('Could not proxy request') >= 0 ||
                    response.body.indexOf('502 Bad Gateway') >= 0 ||
                    response.body.indexOf('504 Gateway Time-out') >= 0
                ) {
                    delete message.body;
                }
            }
            delete response.request;
        }

        if (message && message.body && Object.keys(message.body).length === 0) {
            delete message.body;
        }

        message = JSON.stringify(message, null, 4);
    }
    return message;
}

export const emitToWebservice = async (
    message: any,
    logType: Event.LogTypeEnum,
    eventType: Event.EventTypeEnum = Event.EventTypeEnum.Generic
) => {
    message = beautifyMessageIfError(message);
    const data = { message, logType, eventType };
    try {
        if (authenticationService.getWasAuthenticated()) {
            const defaultApi = await createDefaultApi();
            await defaultApi.emit(data);
        } else {
            if (authenticationService.getLastKnownUser()) {
                data.message = `Last successfully logged in user: ${authenticationService.getLastKnownUser()}
${data.message}`;
            }
            const noSecurityApi = await createNoSecurityApi();
            await noSecurityApi.emit(data);
        }
    } catch (e) {
        console.error(message);
    }
};

export interface StorageInterfaceTranslations {
    storageUnitName: string;
    versioning: string;
}

export function getStorageInterfaceTranslations(objectStorage: string): StorageInterfaceTranslations {
    const isS3 = objectStorage === 's3';
    return {
        storageUnitName: isS3 ? 'bucket' : 'container',
        versioning: isS3 ? 'versioning' : 'soft delete'
    };
}

export function capitalizeFirstLetter(input: string): string {
    if (!input) {
        return input;
    }

    return input[0].toUpperCase() + input.slice(1);
}

export function isIEorEdge(): boolean {
    const userAgent = window.navigator.userAgent;
    return isIE() || userAgent.indexOf('Edge/') > 0;
}

export function isIE(): boolean {
    const userAgent = window.navigator.userAgent;
    return userAgent.indexOf('MSIE ') > 0 || userAgent.indexOf('Trident/') > 0;
}

export enum NewUserLocalStoreFields {
    FIRST_LOGIN = 'event_firstLogin',
    FIRST_WIZARD_OPEN = 'event_firstFsWizardOpen',
    FIRST_FS_ID = 'event_firstFsId',
    FIRST_FS_STATUS = 'event_firstFsStatus'
}

export function isNewUser(): boolean {
    const lastKnownUserId = authenticationService.getLastKnownUserId();
    return !!localStorage.getItem(`${lastKnownUserId}_${NewUserLocalStoreFields.FIRST_FS_STATUS}`) ||
           !!localStorage.getItem(`${lastKnownUserId}_${NewUserLocalStoreFields.FIRST_LOGIN}`) ||
           !!localStorage.getItem(`${lastKnownUserId}_${NewUserLocalStoreFields.FIRST_WIZARD_OPEN}`) ||
           !!localStorage.getItem(`${lastKnownUserId}_${NewUserLocalStoreFields.FIRST_FS_ID}`);
}

export function elementHasClass(domElement: any, className: string) {
    const elementClasses = ` ${domElement.className} `;
    return elementClasses.includes(className);
}

function sleep(ms: any) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export async function copyToClipboard(stringToBeCopied: string): Promise<void> {
    await navigator.clipboard.writeText(stringToBeCopied);
}

export function getCurrencySymbol(currency: string)  {
    switch (currency) {
        case 'usd':
            return '$';
        case 'eur':
            return '€';
        default:
            return currency.toUpperCase();
    }
}

export function formatAmount(amount: number, currencyCode: string = 'usd'): string {
    const currency = getCurrencySymbol(currencyCode);

    return `${currency}${(amount / 100).toFixed(2)}`;
}

export function convertMongoObjectIdToDate(objectId: string): Date {
    const timestamp = objectId.substring(0, 8);

    return new Date(parseInt(timestamp, 16) * 1000);
}

export function isSeenForFirstTime(email: string, storageItem: string): boolean {
    const allTimeEmailsJson = localStorage.getItem(storageItem);
    if (!allTimeEmailsJson) {
        localStorage.setItem(storageItem, JSON.stringify([email]));
        return true;
    }

    const allTimeEmails = JSON.parse(allTimeEmailsJson);
    const isSeen = allTimeEmails.some((userEmail: string) => userEmail === email);
    if (isSeen) {
        return false;
    }

    allTimeEmails.push(email);
    localStorage.setItem(storageItem, JSON.stringify(allTimeEmails));
    return true;
}

export async function waitForElement(id: string, interval: number, retryTimes: number = 10): Promise<Nullable<HTMLElement>> {
    const element = document.getElementById(id);
    if (element) {
        return element;
    }

    retryTimes--;
    if (retryTimes === 0) {

        return null;
    }

    await sleep(interval);
    const result = waitForElement(id, interval, retryTimes);

    return result;
}

export function parseQueryParameters(
    queryParams: string
): [key: string, value: string][] {
    const result: [string, string][] = [];
    new URLSearchParams(queryParams).forEach((value, key) => {
        result.push([key, value]);
    });
    return result;
}

export interface EmailComponents {
    emailName: string;
    emailDomainFull: string[];
    emailProviderName: string;
}

export function splitEmailToComponents(email: string): EmailComponents {
        const emailSplit = email.split('@');
        const emailName = emailSplit[0];
        const emailDomainFull = emailSplit[1] ? emailSplit[1].split('.') : [];
        const emailProviderName = emailDomainFull.slice(0, -1).join('.');

        return {
            emailName,
            emailDomainFull,
            emailProviderName
        };
}

export function pushEventToAnalytics(event: string, params: object = {}) {
    Amplitude.track(event, params);
    
    if (!(window as any).dataLayer) {
        return;
    }

    let eventObject = {
        event,
        ...params,
    };

    (window as any).dataLayer.push(eventObject);
}



// WEBPACK FOOTER //
// ./src/webportal/utils/utilFunctions.ts